import React, { useState, useMemo, useCallback } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import ChevronDownIcon from '@atlaskit/icon/utility/migration/chevron-down';

import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import DisabledButtonWithTooltip from '@atlassian/jira-disabled-button-with-tooltip/src/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { useIssueKey } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldConfig } from '@atlassian/jira-issue-field-base/src/services/field-config-service/main.tsx';
import { PRIORITY } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import { AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import { DEFAULT_SORTING_OPTION, SORT_OPTION, SORT_OPTIONS } from '../../../common/constants.tsx';
import {
	hasExceededIssuesLimitAfterLoad,
	hasExceededIssuesLimitInitialLoad,
} from '../../../common/utils/has-exceeded-issues-limit/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

export const DropdownItemWithAnalytics = AnalyticsEventToProps('dropdownItem', {
	onClick: 'clicked',
})(DropdownItem);

const optionsWithoutDefault = SORT_OPTIONS.filter(
	(sortingOption) => sortingOption !== DEFAULT_SORTING_OPTION,
);

const DEFAULT_OPTION_LABEL = 'defaultOptionLabel';

export const SortByMenu = (props: Props) => {
	const { sortedBy, onSelect, childIssuesLimitStatus } = props;
	const options = optionsWithoutDefault;
	const defaultOption = DEFAULT_SORTING_OPTION;
	let availableSortingOptions = options;

	const issueKey = useIssueKey();
	const { formatMessage } = useIntl();
	const [isOpen, setIsOpen] = useState(false);

	// we only want to allow filter by priority if we have `allowedValues` in context
	const [{ value: priorityField }] = useFieldConfig(issueKey, PRIORITY);

	if (!priorityField || !priorityField.allowedValues || priorityField.allowedValues.length <= 1) {
		availableSortingOptions = options.filter((option) => option !== SORT_OPTION.PRIORITY);
	}

	const optionLabels = useMemo(() => {
		const labelsMap = new Map();
		const defaultOptionName = formatMessage(messages[DEFAULT_SORTING_OPTION]);
		const defaultOptionLabel = formatMessage(messages.defaultOptionLabel, {
			option: defaultOptionName,
		});

		labelsMap.set(DEFAULT_SORTING_OPTION, defaultOptionName);
		labelsMap.set(DEFAULT_OPTION_LABEL, defaultOptionLabel);
		availableSortingOptions.forEach((sortOption) => {
			labelsMap.set(sortOption, formatMessage(messages[sortOption]));
		});

		return labelsMap;
	}, [availableSortingOptions, formatMessage]);

	const otherOptions = useMemo(
		() => availableSortingOptions.filter((option) => option !== sortedBy),
		[availableSortingOptions, sortedBy],
	);

	const hasExceededLimitAfterLoad = hasExceededIssuesLimitAfterLoad(childIssuesLimitStatus);

	const button = useCallback(
		// @ts-expect-error - TS2525 - Initializer provides no value for this binding element and the binding element has no default value.
		({ triggerRef, ...triggerProps } = {}) => (
			<StyledButton
				{...triggerProps}
				appearance="subtle"
				isSelected={isOpen}
				iconAfter={<ChevronDownIcon color="currentColor" label="" />}
				/* eslint-disable-next-line jira/integration/test-id-by-folder-structure */
				testId="issue.views.common.child-issues-panel.heading.sort-by-menu.trigger-button"
				isDisabled={hasExceededLimitAfterLoad}
				ref={triggerRef}
				interactionName="auto-generated.issue.views.common.child-issues-panel.heading.sort-by-menu.trigger-button.button"
				spacing="compact"
			>
				<LabelWrapper>
					{formatMessage(messages.sortByLabel, {
						// label should read just "sort by" whenever the default option is to be used
						option:
							sortedBy !== null && sortedBy !== defaultOption
								? optionLabels.get(sortedBy)?.toLowerCase()
								: '',
					})}
				</LabelWrapper>
			</StyledButton>
		),
		[defaultOption, formatMessage, hasExceededLimitAfterLoad, isOpen, optionLabels, sortedBy],
	);

	if (hasExceededIssuesLimitInitialLoad(childIssuesLimitStatus)) {
		return null;
	}

	if (hasExceededLimitAfterLoad) {
		return (
			<Tooltip
				content={formatMessage(
					fg('jira-issue-terminology-refresh-m3')
						? messages.exceededChildIssuesLimitIssueTermRefresh
						: messages.exceededChildIssuesLimit,
				)}
				position="top"
			>
				<DisabledButtonWithTooltip isDisabled={hasExceededLimitAfterLoad}>
					{button()}
				</DisabledButtonWithTooltip>
			</Tooltip>
		);
	}

	return (
		<DropdownMenu
			trigger={button}
			isOpen={isOpen}
			onOpenChange={() => {
				setIsOpen(!isOpen);
			}}
		>
			<DropdownGroupWrapper>
				<DropdownItemGroup>
					{otherOptions.map((option) => (
						<DropdownItemWithAnalytics
							key={option}
							data-testId={`issue.views.common.child-issues-panel.heading.sort-by-menu.option-${option}`}
							// @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
							onClick={(event, analyticsEvent) => onSelect(option, analyticsEvent)}
						>
							{optionLabels.get(option)}
						</DropdownItemWithAnalytics>
					))}
					{sortedBy && sortedBy !== defaultOption && (
						<DefaultOptionWrapper>
							<DropdownItemWithAnalytics
								key={defaultOption}
								data-testId="issue.views.common.child-issues-panel.heading.sort-by-menu.default-option"
								// @ts-expect-error - TS7006 - Parameter 'event' implicitly has an 'any' type. | TS7006 - Parameter 'analyticsEvent' implicitly has an 'any' type.
								onClick={(event, analyticsEvent) => onSelect(defaultOption, analyticsEvent)}
							>
								{optionLabels.get(DEFAULT_OPTION_LABEL)}
							</DropdownItemWithAnalytics>
						</DefaultOptionWrapper>
					)}
				</DropdownItemGroup>
			</DropdownGroupWrapper>
		</DropdownMenu>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const DropdownGroupWrapper = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	minWidth: `${gridSize * 20}px`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const DefaultOptionWrapper = styled.div({
	borderTop: `1px solid ${token('color.border')}`,
	paddingTop: token('space.050'),
	paddingRight: 0,
	paddingBottom: token('space.050'),
	paddingLeft: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	span: {
		color: token('color.link'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const StyledButton = styled(Button)({
	height: '24px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const LabelWrapper = styled.span({
	paddingRight: token('space.075'),
});
